import { useEffect, useState } from 'react'
import { getFirestore, collection, getDocs, deleteDoc, doc } from 'firebase/firestore'

interface LeadItem {
  id: string
  name: string
  email: string
  phone: string
  source: string
  createdAtMs: number
}

export default function LeadsPage() {
  const [items, setItems] = useState<LeadItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    async function load() {
      try {
        const db = getFirestore()
        const snap = await getDocs(collection(db, 'crm_leads'))
        const list: LeadItem[] = []

        snap.forEach(docSnap => {
          const data = docSnap.data() as any
          const createdAtMs =
            typeof data.createdAt?.toMillis === 'function'
              ? data.createdAt.toMillis()
              : Number(data.order) || 0

          list.push({
            id: docSnap.id,
            name: data.name || 'Sem nome',
            email: data.email || '',
            phone: data.phone || '',
            source: data.source || '',
            createdAtMs,
          })
        })

        list.sort((a, b) => b.createdAtMs - a.createdAtMs)
        setItems(list)
      } catch (err: any) {
        setError('Erro ao carregar leads: ' + err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  async function handleDelete(id: string, name: string) {
    const confirmed = window.confirm(`Excluir o lead "${name}"? Essa ação não pode ser desfeita.`)
    if (!confirmed) return

    setDeletingId(id)
    try {
      const db = getFirestore()
      await deleteDoc(doc(db, 'crm_leads', id))
      setItems(prev => prev.filter(item => item.id !== id))
    } catch (err: any) {
      alert('Erro ao excluir lead: ' + err.message)
    } finally {
      setDeletingId(null)
    }
  }

  const filtered = items.filter(item => {
    const q = search.toLowerCase()
    return (
      item.name.toLowerCase().includes(q) ||
      item.email.toLowerCase().includes(q) ||
      item.phone.includes(search)
    )
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Carregando leads...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold text-white">Leads</h1>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nome, email ou telefone..."
          className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-indigo-500 transition-colors w-72"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-gray-400">
            {items.length === 0 ? 'Nenhum lead capturado ainda.' : 'Nenhum lead encontrado para essa busca.'}
          </p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-gray-400">
                <th className="px-4 py-3 font-medium">Nome</th>
                <th className="px-4 py-3 font-medium">Contato</th>
                <th className="px-4 py-3 font-medium">Origem</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                  <td className="px-4 py-3 text-gray-300">
                    {item.email && <div>{item.email}</div>}
                    {item.phone && <div className="text-gray-500">{item.phone}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-300">
                      {item.source || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(item.id, item.name)}
                      disabled={deletingId === item.id}
                      className="text-red-400 text-xs hover:text-red-300 transition-colors disabled:opacity-50"
                    >
                      {deletingId === item.id ? 'Excluindo...' : 'Excluir'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
